/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { isPast } from 'date-fns'
import { jwtDecode } from 'jwt-decode'

import {
  getTokenFromOldRefreshToken,
  refreshTokenByModernRefresh,
} from '../keycloack-auth.service'

export let KEYCLOAK_ACCESSTOKEN = ''

/**
 * Декодирует Keycloak JWT токен и возвращает его содержимое
 * @param token - Keycloak JWT токен для декодирования
 * @returns Объект с данными токена или null если токен невалидный
 */
export const decodeKeycloakToken = <T = any>(token: string): T | null => {
  try {
    if (!token) {
      return null
    }
    return jwtDecode<T>(token)
  } catch (error) {
    console.error('Ошибка декодирования Keycloak JWT токена:', error)
    return null
  }
}

/**
 * Получает и декодирует Keycloak токен из localStorage
 * @returns Объект с данными токена или undefined если токен недоступен
 */
export const getKeycloakTokenData = <T = any>(): T | undefined => {
  try {
    // Проверяем есть ли токен в localStorage
    const keycloakToken = localStorage.getItem('keycloak-token-access')

    // Если токена нет, возвращаем undefined
    if (!keycloakToken) {
      return undefined
    }

    // Декодируем токен и возвращаем данные
    return decodeKeycloakToken<T>(keycloakToken) || undefined
  } catch (error) {
    console.error('Ошибка при получении Keycloak токена:', error)
    return undefined
  }
}

export class KeycloakTokenManipulate {
  static print(accessToken: string, refreshToken: string) {
    KEYCLOAK_ACCESSTOKEN = accessToken
    localStorage.setItem('keycloak-token', refreshToken)
    localStorage.setItem('keycloak-token-access', accessToken)
  }

  static async refresh(refreshToken: string) {
    const oldRefreshToken = localStorage.getItem('token')
    const refreshTokenBody = refreshToken ? jwtDecode(refreshToken) : null

    if (!oldRefreshToken) {
      throw new Error('Old Refresh token is missing')
    }

    if (!refreshTokenBody || isPast(new Date(refreshTokenBody.exp! * 1000))) {
      return await getTokenFromOldRefreshToken(oldRefreshToken).then(tokens => {
        this.print(tokens.accessToken, tokens.refreshToken)
      })
    }

    const oldRefreshTokenBody = jwtDecode<{ email: string }>(oldRefreshToken)
    const realm = localStorage.getItem('REALM')!

    return await refreshTokenByModernRefresh(
      refreshToken,
      oldRefreshTokenBody.email,
      realm,
    ).then(tokens => {
      this.print(tokens.accessToken, tokens.refreshToken)
    })
  }

  static remove() {
    KEYCLOAK_ACCESSTOKEN = ''
    localStorage.removeItem('keycloak-token')
  }
}

export const checkKeycloakToken = async (config: any) => {
  if (config.url === `/tokens/jwt/refresh`) return config

  const tokenBody = KEYCLOAK_ACCESSTOKEN
    ? jwtDecode(KEYCLOAK_ACCESSTOKEN)
    : null

  if (
    tokenBody &&
    !isPast(tokenBody.exp! * 1000) &&
    !(
      config.headers?.Authorization &&
      config.headers?.Authorization.toString() === 'Bearer '
    )
  ) {
    config.headers.Authorization = `Bearer ${KEYCLOAK_ACCESSTOKEN}`
    return config
  }

  await KeycloakTokenManipulate.refresh(
    localStorage.getItem('keycloak-token')!,
  ).then(() => {
    config.headers.Authorization = `Bearer ${KEYCLOAK_ACCESSTOKEN}`
    return config
  })

  return config
}
